import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";

const ATHLETE_IMG =
  "https://images.unsplash.com/photo-1764347753770-08c46fd4fa0d?fm=jpg&q=80&w=1200&auto=format&fit=crop";
const TEAM_IMG =
  "https://images.unsplash.com/photo-1770876577940-297a5b6f31b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMGZvY3VzZWQlMjBtZWV0aW5nfGVufDB8fHx8MTc3Nzg4OTI3Nnww&ixlib=rb-4.1.0&q=85";
const STUDENT_IMG =
  "https://images.pexels.com/photos/9489774/pexels-photo-9489774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICES = [
  {
    img: ATHLETE_IMG,
    title: "Préparation mentale & consultation en psychologie du sport",
    subtitle: "Sportifs · Compétition / haut-niveau",
    desc:
      "Gestion du stress en compétition, confiance en soi, concentration et performance, motivation, visualisation mentale, retour post-blessure.",
  },
  {
    img: TEAM_IMG,
    title: "Préparation mentale clubs, équipes & entreprises",
    subtitle: "Collectif & groupes",
    desc:
      "Cohésion d'équipe, gestion du stress collectif, performance d'équipe, ateliers en groupe, teambuilding.",
  },
  {
    img: STUDENT_IMG,
    title: "Préparation mentale étudiants",
    subtitle: "Concours & examens",
    desc:
      "Préparation aux concours, concentration, gestion du stress, récupération et repos.",
  },
  {
    icon: TrendingUp,
    title: "Accompagnement longue durée",
    subtitle: "3, 6 ou 12 mois",
    desc:
      "Un partenariat sur 3, 6 ou 12 mois pour les échéances décisives : championnat d'envergure, lancement de business, examens majeurs.",
    points: [
      "Suivi hebdomadaire",
      "Bilans trimestriels",
      "Plan d'action personnalisé et évolutif",
    ],
  },
];

const METHODE = [
  {
    n: "01",
    title: "Analyse des besoins",
    desc: "Comprendre les raisons de la venue, le contexte et les attentes.",
  },
  {
    n: "02",
    title: "Définition des objectifs",
    desc: "Construire ensemble une feuille de route claire et réaliste.",
  },
  {
    n: "03",
    title: "Présentation des outils",
    desc:
      "Familiarisation avec les techniques utilisées : respiration, visualisation, routines.",
  },
  {
    n: "04",
    title: "Mise en pratique",
    desc: "Application sur le terrain, en entraînement et en compétition.",
  },
  {
    n: "05",
    title: "Suivi & autonomie",
    desc:
      "Ajustement progressif jusqu'à ce que les outils soient mobilisés en autonomie.",
  },
];

export const Services = () => {
  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#FAF6F1]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl mb-16">
          <span className="text-base md:text-lg font-bold uppercase tracking-[0.18em] text-[#9B8FE0]">
            Services
          </span>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
            Notre fonctionnement.
          </h2>
          <p className="mt-8 text-zinc-600 text-xl md:text-2xl leading-relaxed border-l-4 border-[#9B8FE0] pl-6">
            Chaque accompagnement en préparation mentale est conçu pour
            s'adapter au rythme, au niveau d'engagement et aux objectifs de
            la personne&nbsp;: gestion du stress, confiance en soi,
            performance sportive ou développement des ressources mentales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.title}
              href="#tarifs"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`service-card-${i}`}
              className="group block bg-white border border-zinc-200 rounded-3xl overflow-hidden hover:border-[#9B8FE0]/50 hover:shadow-[0_18px_50px_-20px_rgba(155,143,224,0.4)] hover:-translate-y-1 transition-all cursor-pointer"
            >
              {s.img ? (
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-[#9B8FE0] to-[#B5A8EC] flex items-center justify-center">
                  {s.icon && <s.icon size={56} className="text-white" />}
                </div>
              )}

              <div className="p-8 md:p-10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm md:text-base font-bold uppercase tracking-[0.15em] text-[#9B8FE0]">
                      {s.subtitle}
                    </div>
                    <h3 className="font-display mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                      {s.title}
                    </h3>
                  </div>
                  <ArrowUpRight
                    size={26}
                    className="text-zinc-400 group-hover:text-[#9B8FE0] group-hover:rotate-45 transition-all shrink-0"
                  />
                </div>
                <p className="mt-4 text-lg md:text-xl text-zinc-700 leading-relaxed">
                  {s.desc}
                </p>
                {s.points && (
                  <ul className="mt-5 space-y-2">
                    {s.points.map((p) => (
                      <li
                        key={p}
                        className="text-base md:text-lg text-zinc-700 flex items-center gap-2"
                      >
                        <span className="w-2 h-2 rounded-full bg-[#9B8FE0]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-6 inline-flex items-center gap-2 text-base font-semibold text-[#9B8FE0] opacity-0 group-hover:opacity-100 transition-opacity">
                  Voir les tarifs <ArrowUpRight size={14} className="rotate-45" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Méthode */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mt-20 md:mt-24"
        >
          <div className="max-w-2xl mb-12">
            <span className="text-base md:text-lg font-bold uppercase tracking-[0.18em] text-[#9B8FE0]">
              Méthode
            </span>
            <h3 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
              Comment je travaille, étape par étape.
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {METHODE.map((m, i) => (
              <motion.div
                key={m.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`methode-step-${i}`}
                className="bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#9B8FE0]/60 transition-colors"
              >
                <div className="font-display text-3xl md:text-4xl font-black text-[#9B8FE0]">
                  {m.n}
                </div>
                <div className="mt-4 font-display text-xl md:text-2xl font-bold tracking-tight">
                  {m.title}
                </div>
                <p className="mt-2 text-base md:text-lg text-zinc-600 leading-relaxed">
                  {m.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
